import React from 'react';
import { Autocomplete, Box, OptionTile, Text } from '@medely/ui-kit';
import { ReleaseShiftOptionsProps, ReleaseStep } from './interface';
import { ReleaseStatus } from '../interface';
import { useSwapProfessionalsList } from './graphql';
import { ContentLoader } from '@mp/components/ContentLoader';

type ReleaseShiftModalInfoProps = {
  handleStatusChange: (status: ReleaseStatus) => void;
  currentStatus: ReleaseStatus | null;
  currentStep: ReleaseStep;
  releaseOptions: ReleaseShiftOptionsProps[];
  getRequesteeId: (id: number) => void;
  jobId?: number | null;
};

const ReleaseShiftOptionsStep = ({
  currentStatus,
  handleStatusChange,
  releaseOptions,
}: Pick<ReleaseShiftModalInfoProps, 'currentStatus' | 'handleStatusChange' | 'releaseOptions'>) => {
  return (
    <OptionTile
      options={releaseOptions}
      value={currentStatus}
      onChange={(value) => handleStatusChange(value)}
      variant="text"
    />
  );
};

const ChooseProToSwapStep = ({
  getRequesteeId,
  jobId,
}: Pick<ReleaseShiftModalInfoProps, 'getRequesteeId' | 'jobId'>) => {
  const { availableProfessionals, isLoading } = useSwapProfessionalsList({ jobId });

  const proOptions = availableProfessionals.map((pro) => ({
    label: pro.name as string,
    value: pro.id,
  }));

  const hasNoAvailablePros = !availableProfessionals.length && !isLoading;

  const [selectedPro, setSelectedPro] = React.useState<{ label: string; value: number } | null>(
    null,
  );

  return (
    <ContentLoader loading={isLoading}>
      <Box py={1}>
        <Autocomplete
          options={proOptions}
          value={selectedPro}
          onChange={(option) => {
            setSelectedPro(option as { label: string; value: number } | null);
            if (option && typeof option.value === 'number') {
              getRequesteeId(option.value);
            }
          }}
          placeholder={hasNoAvailablePros ? 'No available professionals for swap' : 'Staff'}
          disabled={hasNoAvailablePros}
        />
      </Box>
    </ContentLoader>
  );
};

export const ReleaseShiftModalInfo = ({
  handleStatusChange,
  currentStatus,
  currentStep,
  releaseOptions,
  getRequesteeId,
  jobId,
}: ReleaseShiftModalInfoProps) => {
  const isReleaseOptionsStep = currentStep === 'releaseOptions';

  const textInfo = isReleaseOptionsStep
    ? 'This facility allows for the following options.'
    : 'Select the staff member you want to release the shift to.';

  return (
    <>
      <Box pb={1}>
        <Text size="m">{textInfo}</Text>
      </Box>
      {isReleaseOptionsStep ? (
        <ReleaseShiftOptionsStep
          currentStatus={currentStatus}
          handleStatusChange={handleStatusChange}
          releaseOptions={releaseOptions}
        />
      ) : (
        <ChooseProToSwapStep getRequesteeId={getRequesteeId} jobId={jobId} />
      )}
    </>
  );
};
